import { Link } from 'react-router-dom'
import { Mail, Phone, MapPin, Shield, ArrowRight, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react'
import logo from '../assets/logo.png'

const Footer = () => {
  const currentYear = new Date().getFullYear()

  const products = [
    { name: 'Payment Gateway', href: '/products' },
    { name: 'Bill Payments', href: '/products' },
    { name: 'Mobile Payments', href: '/products' },
    { name: 'E-Commerce Plugins', href: '/products' },
  ]

  const company = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about' },
    { name: 'FAQs', href: '/faqs' },
    { name: 'Contact Us', href: '/contact' },
    { name: 'Get Started', href: '/get-started' },
  ]

  const legal = [
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms of Service', href: '/terms' },
    { name: 'Security', href: '/security' },
    { name: 'Compliance', href: '/compliance' },
  ]

  const socials = [
    { icon: <Facebook className="h-4 w-4" />, href: '#', label: 'Facebook' },
    { icon: <Twitter className="h-4 w-4" />, href: '#', label: 'Twitter' },
    { icon: <Linkedin className="h-4 w-4" />, href: '#', label: 'LinkedIn' },
    { icon: <Instagram className="h-4 w-4" />, href: '#', label: 'Instagram' },
  ]

  return (
    <footer className="bg-gray-900 text-white">
      {/* Top CTA strip */}
      <div className="bg-orange-500">
        <div className="container-max section-padding py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="font-bold text-white text-lg">Ready to go digital?</p>
            <p className="text-orange-100 text-sm">Join thousands of businesses already on MobiPay.</p>
          </div>
          <Link
            to="/get-started"
            className="flex-shrink-0 inline-flex items-center bg-white text-orange-600 hover:bg-orange-50 px-6 py-3 rounded-lg font-bold text-sm transition-all duration-200 shadow-md"
          >
            Get Started Free <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Main footer grid */}
      <div className="container-max section-padding py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">

          {/* Brand – spans 2 cols on lg */}
          <div className="lg:col-span-2 space-y-5">
            <Link to="/" className="inline-block">
              <img src={logo} alt="MobiPay" className="h-10 w-auto brightness-0 invert" />
            </Link>
            <p className="text-gray-400 leading-relaxed text-sm max-w-xs">
              MobiPay helps businesses accept and manage digital payments through a variety of online payment methods.
            </p>

            {/* Compliance badges */}
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-1.5 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-xs text-gray-300">
                <Shield className="h-3.5 w-3.5 text-orange-400" /> Visa Partner
              </div>
              <div className="flex items-center gap-1.5 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-xs text-gray-300">
                <Shield className="h-3.5 w-3.5 text-orange-400" /> Fraud Protection
              </div>
              <div className="flex items-center gap-1.5 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-xs text-gray-300">
                <Shield className="h-3.5 w-3.5 text-orange-400" /> 256-bit Encryption
              </div>
            </div>

            {/* Social links */}
            <div className="flex items-center gap-3 pt-1">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  className="w-9 h-9 bg-gray-800 hover:bg-orange-500 border border-gray-700 hover:border-orange-500 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200"
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="font-bold text-white text-sm uppercase tracking-wider mb-5">Products</h3>
            <ul className="space-y-3">
              {products.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-orange-400 text-sm transition-colors duration-200 flex items-center gap-1.5 group"
                  >
                    <span className="w-1 h-1 bg-gray-600 group-hover:bg-orange-400 rounded-full transition-colors flex-shrink-0" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-bold text-white text-sm uppercase tracking-wider mb-5">Company</h3>
            <ul className="space-y-3">
              {company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-orange-400 text-sm transition-colors duration-200 flex items-center gap-1.5 group"
                  >
                    <span className="w-1 h-1 bg-gray-600 group-hover:bg-orange-400 rounded-full transition-colors flex-shrink-0" />
                    {link.name}
                  </Link>
                </li>
              ))}
              {legal.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-orange-400 text-sm transition-colors duration-200 flex items-center gap-1.5 group"
                  >
                    <span className="w-1 h-1 bg-gray-600 group-hover:bg-orange-400 rounded-full transition-colors flex-shrink-0" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-bold text-white text-sm uppercase tracking-wider mb-5">Contact Us</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Mail className="h-4 w-4 text-orange-400" />
                </div>
                <div>
                  <p className="text-gray-500 text-xs mb-0.5">Email</p>
                  <a href="mailto:support@mobipay.pk" className="text-gray-300 hover:text-orange-400 text-sm transition-colors">
                    support@mobipay.pk
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Phone className="h-4 w-4 text-orange-400" />
                </div>
                <div>
                  <p className="text-gray-500 text-xs mb-0.5">Phone</p>
                  <a href="tel:+923438018055" className="text-gray-300 hover:text-orange-400 text-sm transition-colors">
                    +92 343 8018055
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin className="h-4 w-4 text-orange-400" />
                </div>
                <div>
                  <p className="text-gray-500 text-xs mb-0.5">Head Office</p>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Khawaja House, Chowk BCG,<br />Multan
                  </p>
                  <p className="text-gray-500 text-xs mt-2 mb-0.5">Branch Office</p>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Garden Heights, Garden Town,<br />Lahore
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-800">
        <div className="container-max section-padding py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-gray-500 text-xs text-center sm:text-left">
            © {currentYear} MobiPay. All rights reserved.
          </p>
          <p className="text-gray-500 text-xs text-center">Built for modern digital payments.</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-gray-500 hover:text-orange-400 text-xs transition-colors">Privacy</Link>
            <Link to="/terms" className="text-gray-500 hover:text-orange-400 text-xs transition-colors">Terms</Link>
            <Link to="/security" className="text-gray-500 hover:text-orange-400 text-xs transition-colors">Security</Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
